/**
 * @file WithMESOverview.jsx
 * @description 제조실행시스템(MES) 소개 페이지입니다.
 */

import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  ClipboardList,
  Database,
  Factory,
  FileText,
  Layers,
  Scale,
  Settings,
  Target,
} from 'lucide-react';
import { solMes } from '../../assets';
import { solutionItems } from '../../constants/solutionData';

const MES_DATA = solutionItems.find((item) => item.id === 'mes');

const SUMMARY_ICONS = [Target, Settings, Database];
const CAPABILITY_META = [
  { category: 'PRODUCTION ORDER', Icon: ClipboardList },
  { category: 'PROCESS CONTROL', Icon: BarChart3 },
  { category: 'DATA COLLECTION', Icon: Layers },
  { category: 'E-BATCH RECORD', Icon: FileText },
];

const CAPABILITIES = MES_DATA.keyModules.map((module, index) => ({
  num: String(index + 1).padStart(2, '0'),
  category: CAPABILITY_META[index].category,
  title: module.label,
  Icon: CAPABILITY_META[index].Icon,
  desc: module.desc ?? MES_DATA.features[index],
}));

const WEIGHING_BARS = [
  { day: '월', total: 40, done: 28 },
  { day: '화', total: 45, done: 32 },
  { day: '수', total: 38, done: 30 },
  { day: '목', total: 51, done: 36 },
  { day: '금', total: 43, done: 34 },
];

const CHART_TICKS = [52, 45, 40, 35, 30, 25, 20, 15, 10, 5, 0];

const WEIGHING_ROWS = [
  { lot: 'P260418-001', process: '칭량', status: '진행중', itemName: 'Lactose Monohydrate', itemCode: 'RM-1001', qty: '12.5 kg' },
  { lot: 'P260418-002', process: '칭량', status: '대기', itemName: 'Microcrystalline Cellulose', itemCode: 'RM-1002', qty: '8.0 kg' },
  { lot: 'P260419-001', process: '칭량', status: '진행중', itemName: 'Povidone K30', itemCode: 'RM-1010', qty: '1.2 kg' },
  { lot: 'P260420-001', process: '칭량', status: '진행중', itemName: 'Sodium Starch Glycolate', itemCode: 'RM-1031', qty: '2.1 kg' },
];

const PROCESS_MONTHS = [
  { month: '03월', rate: 79, total: 128, done: 101, pending: 27 },
  { month: '04월', rate: 82, total: 146, done: 119, pending: 27 },
  { month: '05월', rate: 59, total: 92, done: 54, pending: 38 },
];

const PROCESS_ROWS = [
  { type: '포장', lot: 'TEST112', status: '지시', item: '새힘 완제품 주사제 001', plan: '110 EA' },
  { type: '포장', lot: 'TEST112', status: '지시', item: '새힘 완제품 주사제 001', plan: '110 EA' },
  { type: '제조', lot: 'WOTEST001', status: '지시', item: '새힘 반제품 고형제 001', plan: '150 EA' },
  { type: '제조', lot: 'WOTEST001', status: '진행', item: '새힘 반제품 고형제 001', plan: '150 EA' },
  { type: '제조', lot: 'WOTEST002', status: '지시', item: '새힘 반제품 고형제 001', plan: '150 EA' },
  { type: '제조', lot: 'WOTEST002', status: '완료', item: '새힘 반제품 고형제 001', plan: '150 EA' },
];

const PROCESS_STEPS = [
  { n: '01', title: '생산 지시', desc: '생산 계획을 공정별 작업 오더로 전환' },
  { n: '02', title: '공정 진행', desc: '작업 시작, 진행, 완료 상태를 실시간 수집' },
  { n: '03', title: '실적 수집', desc: '생산량, 설비, 품질 검사 결과를 연결' },
  { n: '04', title: '이상 조치', desc: '지연과 이상 발생 이력을 조치 정보로 관리' },
  { n: '05', title: '기록 마감', desc: '전자 배치기록과 리포트로 제조 이력 보존' },
];

function MESHero() {
  return (
    <section className="rws-hero">
      <div className="container">
        <div className="rws-hero-inner">
          <div className="rws-hero-visual">
            <img src={solMes} alt="MES 제조실행 시스템 화면 예시" className="rws-hero-image" />
          </div>

          <div className="rws-hero-text">
            <p className="rws-eyebrow">{MES_DATA.badge}</p>
            <h1 className="rws-hero-title">
              {MES_DATA.portfolioTitle[0]}
              <br />
              <em className="rws-accent">{MES_DATA.portfolioTitle[1]}</em>
            </h1>
            <p className="rws-hero-desc">{MES_DATA.summary}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function CapabilityCard({ item }) {
  const Icon = item.Icon;

  return (
    <article className="rws-cap-card">
      <span className="rws-cap-num">{item.num}</span>
      <Icon size={28} color="var(--rws-accent)" strokeWidth={1.8} aria-hidden="true" />
      <p className="rws-cap-category">{item.category}</p>
      <h3 className="rws-cap-title">{item.title}</h3>
      <p className="rws-cap-desc">{item.desc}</p>
    </article>
  );
}

function MESCapabilities() {
  return (
    <section className="rws-caps">
      <div className="container">
        <div className="rws-caps-header">
          <p className="rws-eyebrow">CORE CAPABILITIES</p>
          <h2 className="rws-caps-title">
            {MES_DATA.title}의 <em className="rws-accent">핵심 모듈</em>
          </h2>
          <p className="rws-caps-intro">{MES_DATA.portfolioDesc}</p>
        </div>

        <div className="rws-caps-grid">
          {CAPABILITIES.map((cap) => (
            <CapabilityCard key={cap.num} item={cap} />
          ))}
        </div>
      </div>
    </section>
  );
}

function WeighingDashboard() {
  const maxTotal = 52;

  return (
    <div className="rws-dashboard mes-mini-dashboard" aria-label="MES 칭량 현황 대시보드">
      <div className="rws-bar-chart" aria-label="일별 전체건수와 완료건수 막대 그래프">
        <div className="rws-chart-plot">
          <div className="rws-chart-y-axis" aria-hidden="true">
            {CHART_TICKS.map((tick) => <span key={tick}>{tick}</span>)}
          </div>
          <div className="rws-chart-bars">
            {WEIGHING_BARS.map((item) => (
              <div key={item.day} className="rws-chart-day">
                <div className="rws-chart-pair">
                  <span className="rws-chart-bar is-total" style={{ '--bar-size': `${(item.total / maxTotal) * 100}%` }} />
                  <span className="rws-chart-bar is-done" style={{ '--bar-size': `${(item.done / maxTotal) * 100}%` }} />
                </div>
                <strong>{item.day}</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="rws-chart-legend">
          <span><i className="is-total" /> 전체건수</span>
          <span><i className="is-done" /> 완료건수</span>
        </div>
      </div>

      <div className="rws-weighing-list">
        <div className="rws-list-title-row"><h4>칭량작업 현황</h4></div>
        <div className="rws-work-table">
          <div className="rws-table-head">
            <span>제조번호</span><span>공정</span><span>상태</span><span>품목명</span><span>품목코드</span><span>지시량</span>
          </div>
          {WEIGHING_ROWS.map((row) => (
            <div key={`${row.lot}-${row.itemCode}`} className="rws-table-row">
              <span>{row.lot}</span><span>{row.process}</span><span>{row.status}</span><span>{row.itemName}</span><span>{row.itemCode}</span><span>{row.qty}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProcessDashboard() {
  return (
    <div className="mes-process-dashboard" aria-label="MES 공정현황 대시보드">
      <div className="mes-process-panel">
        <h3>공정현황 <span>(최근 3개월)</span></h3>
        <div className="mes-month-grid">
          {PROCESS_MONTHS.map((item) => (
            <article key={item.month} className="mes-month-card">
              <h4>{item.month}</h4>
              <div className="mes-donut" style={{ '--rate': `${item.rate}%` }}>
                <strong>{item.rate}%</strong>
              </div>
              <div className="mes-month-labels"><span>전체</span><span>완료</span><span>미완료</span></div>
              <div className="mes-month-values"><strong>{item.total}</strong><strong>{item.done}</strong><strong>{item.pending}</strong></div>
            </article>
          ))}
        </div>
      </div>

      <div className="mes-process-list">
        <h3>공정작업 현황</h3>
        <div className="mes-process-table">
          <div className="mes-process-head"><span>구분</span><span>제조번호</span><span>공정현황</span><span>품목명</span><span>제조량</span></div>
          {PROCESS_ROWS.map((row, index) => (
            <div key={`${row.lot}-${index}`} className="mes-process-row">
              <span>{row.type}</span><span>{row.lot}</span><span>{row.status}</span><span>{row.item}</span><span>{row.plan}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MESDashboardSection() {
  return (
    <section className="rws-proposal rws-proposal-dashboard">
      <div className="container">
        <div className="rws-proposal-inner mes-dashboard-inner">
          <div className="rws-proposal-text">
            <p className="rws-eyebrow">MANUFACTURING CONTROL ROOM</p>
            <h2 className="rws-proposal-title">
              칭량과 공정 현황을 <em className="rws-accent">한 흐름으로 모니터링</em>
            </h2>
            <p className="rws-proposal-desc">{MES_DATA.highlights[2].text}</p>
            <div className="rws-value-list">
              <div><Scale size={18} /> 원료 칭량 현황과 작업 리스트 확인</div>
              <div><Factory size={18} /> 최근 3개월 공정 완료율 추적</div>
              <div><AlertTriangle size={18} /> 지연 공정과 미완료 작업을 빠르게 구분</div>
            </div>
          </div>

          <div className="mes-dashboard-stack">
            <WeighingDashboard />
            <ProcessDashboard />
          </div>
        </div>
      </div>
    </section>
  );
}

function MESProcessSection() {
  return (
    <section className="rws-proposal rws-proposal-process">
      <div className="container">
        <div className="rws-process-head">
          <p className="rws-eyebrow">STANDARD MANUFACTURING FLOW</p>
          <h2 className="rws-proposal-title">
            생산 지시부터 <em className="rws-accent">기록 마감까지</em>
          </h2>
          <p className="rws-proposal-desc">{MES_DATA.portfolioDesc}</p>
        </div>

        <div className="rws-process-steps">
          <div className="rws-process-line" aria-hidden="true" />
          {PROCESS_STEPS.map((step) => (
            <article key={step.n} className="rws-process-step">
              <span className="rws-process-num">{step.n}</span>
              <h3>{step.title}</h3>
              <p>{step.desc}</p>
            </article>
          ))}
        </div>

        <div className="rws-before-after">
          <div className="rws-compare-col">
            <p className="rws-compare-label">Before</p>
            <h3>계획과 현장 실행의 분리</h3>
            <ul>
              <li>공정 진행 상황 확인 지연</li>
              <li>실적과 품질 데이터의 수기 취합</li>
              <li>작업 이력과 배치기록 추적 부담</li>
            </ul>
          </div>
          <div className="rws-compare-arrow" aria-hidden="true"><ArrowRight size={24} /></div>
          <div className="rws-compare-col is-after">
            <p className="rws-compare-label">After</p>
            <h3>공정 중심의 제조 실행 관리</h3>
            <ul>
              <li>공정별 진행률과 작업 현황 실시간 확인</li>
              <li>생산·설비·품질 데이터 연결</li>
              <li>전자 배치기록 기반 감사 추적성 확보</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function MESFinalInfo() {
  return (
    <section className="rws-final-info">
      <div className="container">
        <div className="rws-final-head">
          <p className="rws-eyebrow">MANUFACTURING EXECUTION SUMMARY</p>
          <h2 className="rws-final-title">
            제조 실행의 <em className="rws-accent">운영 기준</em>을 한 번에 정리합니다.
          </h2>
        </div>
        <div className="sol-info-grid fade-in rws-info-grid">
          <div className="sol-summary-stack">
            {MES_DATA.highlights.map((item, index) => {
              const Icon = SUMMARY_ICONS[index] ?? Target;
              return (
                <article key={item.label} className="sol-summary-card">
                  <div className="sol-summary-icon" aria-hidden="true"><Icon size={30} strokeWidth={1.8} /></div>
                  <div><h4>{item.label}</h4><p>{item.text}</p></div>
                </article>
              );
            })}
          </div>
          <article className="sol-list-card"><h3 className="sol-list-card-title">주요 기능</h3><ul className="sol-card-list">{MES_DATA.features.map((item) => <li key={item}>{item}</li>)}</ul></article>
          <article className="sol-list-card"><h3 className="sol-list-card-title">도입 효과</h3><ul className="sol-card-list">{MES_DATA.effects.map((item) => <li key={item}>{item}</li>)}</ul></article>
        </div>
      </div>
    </section>
  );
}

export default function WithMESOverview() {
  return (
    <div className="rws-solution-theme mes-solution-theme">
      <MESHero />
      <MESCapabilities />
      <MESDashboardSection />
      <MESProcessSection />
      <MESFinalInfo />
    </div>
  );
}
